import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { BiCheckCircle } from 'react-icons/bi';
import { useRecoilState } from 'recoil';

import { EventDetails } from '@/hooks/useClickEventTracking';
import { getScreenName } from '@/hooks/usePageTracking';

import Sliders from '@/components/CartPage/PaymentSuccessful/Sliders';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import {
  subscriptionRazorpayCloseModalEvent,
  subscriptionSuccessfulEvent,
} from '@/components/shared/Gtags';
import { notify } from '@/components/shared/notify';

import { getSubPlanNameLog } from '@/pages/gems-transactions';
import {
  plansOffer,
  startSubscription,
  verifySubscription,
} from '@/services/Subscribe';
import { getUserProfile } from '@/services/user';
import { EventV2, IEvent } from '@/utils/Events/events';
import {
  IPlansOffer,
  IStartSubscriptionBody,
} from '@/utils/interfaces/Subscription';
import { globalSubscriptionEvent } from '@/utils/Subscription';

import { ShouldRender } from '../shared/basic/ShouldRender';
import CustomLoader from '../shared/Loading';
import { user } from '../../atoms/user';
import { CartItem } from '../../utils/interfaces/cart';
import { FormattedFinalOrder } from '../../utils/interfaces/Orders';

interface Props {
  data?: CartItem[];
  total?: number;
  loading?: boolean;
  order?: FormattedFinalOrder;
  loadingBook?: boolean;
  books?: {
    _id: string;
    coverImageUrl: string;
    title: string;
    externalLink: string;
    type: string;
    slug: string;
    totalPages: number;
    isExternalLink: boolean;
  }[];
}

const PaymentSuccessful: React.FC<Props> = ({ loading, order }) => {
  const pathname = usePathname();
  const router = useRouter();
  const [plansOfferData, setPlansOfferData] = useState<IPlansOffer>();
  const [time, setTime] = useState<number>(300); // 5 minutes = 300 seconds
  const intervalRef = useRef<NodeJS.Timeout | null>(null); // type the interval correctly
  const [loadingSb, setLoadingSb] = useState<boolean>(false);
  const [userData, setUserData] = useRecoilState(user);
  const [, setSubscriptionSuccess] = useState(false);

  const plansOfferHandle = async () => {
    try {
      const result = await plansOffer();
      setPlansOfferData(result.data);
    } catch (error) {
      console.error(error);
    }
  };
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (typeof document === 'undefined') {
        resolve(false);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const physical =
    plansOfferData?.plan?.details?.features?.[0]?.periodicity &&
    plansOfferData?.plan?.details.rules[0]?.physical;

  // const digital = plansOfferData?.plan?.details.features[0]?.periodicity && plansOfferData?.plan?.details.rules[0]?.digital;

  const checkoutEvent = (data: {
    subscription_id: string;
    plan_id: number;
  }) => {
    try {
      const eventParams: IEvent = {
        event_name: 'subscription_checkout',
        source: 'subscription',
        source_type: physical ? 'hardcopy' : 'digital',
        source_id: data?.subscription_id.toString(),
        sub_source: 'plan',
        sub_source_id: data.plan_id.toString(),
        sub_source_name: getSubPlanNameLog(
          plansOfferData?.plan?.details?.name ?? '',
        ),
        source_name: 'product_subscription',
        ...globalSubscriptionEvent(plansOfferData?.plan?.details)?.event,
        meta: {
          click_source: getScreenName(pathname),
          ...globalSubscriptionEvent(plansOfferData?.plan?.details)?.meta,
        },
      };


      EventV2({
        payload: eventParams,
        userData,
        pathname,
      })
    } catch (error) {
      console.error(error);
    }
  };

  const razorpayCloseModalEvent = (data: {
    subscription_id: string;
    plan_id: number;
  }) => {
    try {
      const eventParams: EventDetails = {
        event_name: 'subscription_failed',
        source: 'subscription',
        source_type: physical ? 'hardcopy' : 'digitalcopy',
        source_id: data?.subscription_id.toString(),
        sub_source: 'plan',
        sub_source_id: data.plan_id.toString(),
        sub_source_name: getSubPlanNameLog(
          plansOfferData?.plan?.details?.name ?? '',
        ),
        source_name: 'product_subscription',
        ...globalSubscriptionEvent(plansOfferData?.plan?.details)?.event,
        meta: {
          click_source: getScreenName(pathname),
          ...globalSubscriptionEvent(plansOfferData?.plan?.details)?.meta,
        },
      };

      subscriptionRazorpayCloseModalEvent({
        payload: eventParams,
        userData,
        pathname,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const successEvent = (data: { subscription_id: string }) => {
    try {
      const eventParams: EventDetails = {
        event_name: 'subscription_successful',
        source: 'subscription',
        source_type: physical ? 'hardcopy' : 'digital',
        source_id: data.subscription_id,
        sub_source: 'plan',
        sub_source_id: plansOfferData?.plan?.details?.id.toString(),
        sub_source_name: getSubPlanNameLog(
          plansOfferData?.plan?.details?.name ?? '',
        ),
        source_name: 'product_subscription',
        ...globalSubscriptionEvent(plansOfferData?.plan?.details)?.event,
        meta: {
          click_source: getScreenName(pathname),
          ...globalSubscriptionEvent(plansOfferData?.plan?.details)?.meta,
        },
      };

      subscriptionSuccessfulEvent({
        payload: eventParams,
        userData,
        pathname,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubscribe = async (data: IStartSubscriptionBody) => {
    try {
      setLoadingSb(true);
      const scriptLoaded = await loadRazorpayScript();

      if (!scriptLoaded) {
        notify('Razorpay SDK failed to load. Are you online?', 'error');
        return;
      }

      const result = await startSubscription(data);
      checkoutEvent({
        plan_id: plansOfferData?.plan?.details?.id as number,
        subscription_id: result?.data?.subscription_id.toString(),
      });
      if (!process.env.NEXT_PUBLIC_RAZORPAY_KEY) {
        throw new Error('Missing RAZORPAY_KEY');
      }

      const options = {
        key: process.env.NEXT_PUBLIC_RAZORPAY_KEY || '',
        ...(result.data.mode === 'auto'
          ? { order_id: result.data.razorpay.subscription_id }
          : { order_id: result.data.razorpay.order_id }),
        name: 'Zebralearn',
        description: 'Subscription',
        prefill: {
          name: userData?.name,
          email: userData?.email,
          contact: (userData?.country_code || '') + (userData?.phone || ''),
        },
        handler: async function (response: {
          razorpay_payment_id: string;
          razorpay_subscription_id: string;
          razorpay_signature: string;
        }) {
          setLoadingSb(true);
          successEvent({
            subscription_id: result?.data?.subscription_id.toString()
          });
          // 3. Verify payment
          await verifySubscription({
            rp_payment_id: response.razorpay_payment_id,
          });

          await startSubscriptionHandle();

          await successSubscription();
        },
        modal: {
          ondismiss: function () {
            razorpayCloseModalEvent({
              plan_id: plansOfferData?.plan?.details?.id as number,
              subscription_id:
                result.data.mode === 'auto'
                  ? result.data.razorpay.subscription_id
                  : result.data.razorpay.order_id,
            });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setLoadingSb(false);
    }
  };

  const successSubscription = async () => {
    setSubscriptionSuccess(true);

    const data = await getUserProfile();
    setUserData(data?.data);
    // setLoadingSb(false);

    setTimeout(() => {
      router.push('/byob');
    }, 3000);
  };

  const startSubscriptionHandle = async () => {
    try {
      setLoadingSb(true);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    plansOfferHandle();
  }, []);

  const totalBook =
    plansOfferData?.plan?.details?.periodicity &&
      plansOfferData?.plan?.details?.features?.[0]?.quota
      ? plansOfferData?.plan?.details?.periodicity *
      plansOfferData?.plan?.details?.features?.[0]?.quota
      : 0;

  const array = Math.max(
    totalBook - (plansOfferData?.order?.counts?.total || 0),
    0,
  );

  const productsArray =
    plansOfferData &&
      plansOfferData.order &&
      plansOfferData.order.products &&
      plansOfferData.type !== 'cod'
      ? [...plansOfferData.order.products, ...Array(array).fill(null)]
      : plansOfferData &&
        plansOfferData.order &&
        plansOfferData.order.products &&
        plansOfferData.type === 'cod'
        ? [...Array(array).fill(null)]
        : [];

  const products = productsArray || [];

  const maxItems = 2;

  const limitedProducts = products.slice(0, maxItems);

  const remainingCount =
    products?.length > maxItems ? products?.length - maxItems : 0;

  const startTimer = () => {
    if (intervalRef.current) return; // prevent multiple intervals
    intervalRef.current = setInterval(() => {
      setTime((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          intervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Format seconds to MM:SS
  const formatTime = (seconds: number) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  useEffect(() => {
    startTimer();
  }, []);

  return (
    <>
      <ShouldRender check={loading}>
        <div className='flex justify-center  py-40'>
          <CustomLoader />
        </div>
      </ShouldRender>
      <ShouldRender check={!loading}>
        <div className='lg:pt-[112px] pt-[40px] lg:pb-[50px] text-center flex items-center justify-center gap-y-4 relative bg-gradient-to-b from-readerLightGrayBg to-transparent'>
          <div className=' relative z-10 flex flex-col items-center'>
            <div className='flex flex-col justify-center sm:mb-10 mb-5'>
              <div className='flex items-center sm:gap-3 gap-2 sm:mb-2 mb-1.5 justify-center'>
                <BiCheckCircle className='sm:w-6 w-5 sm:h-6 h-5 text-success-500' />
                <p className='text-white sm:text-2xl text-xl font-bold'>
                  Your order is confirmed.
                </p>
              </div>
              <div className='flex items-center sm:gap-5 gap-0.5 justify-center sm:flex-row flex-col'>
                <p className='text-white sm:text-base text-sm font-normal'>
                  Email: <span className='font-bold'>{userData?.email}</span>
                </p>
                <p className='text-white sm:text-base text-sm font-normal'>
                  Order ID: <span className='font-bold'>{order?.orderId}</span>
                </p>
              </div>
            </div>
            <Sliders
              order={order as FormattedFinalOrder}
              remainingCount={remainingCount}
              limitedProducts={limitedProducts}
              formatTime={formatTime}
              time={time}
              loadingSb={loadingSb}
              handleSubscribe={handleSubscribe}
              plansOfferData={plansOfferData}
              array={array}
              productsArray={productsArray}
            />
          </div>
        </div>
      </ShouldRender>
    </>
  );
};

export default PaymentSuccessful;
